import { __ } from '@wordpress/i18n';
import {
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
} from '@wordpress/block-editor';
import { TextControl, ToggleControl, Button, Flex } from '@wordpress/components';

import PanelBody from '../../components/PanelBody';
import InsertImageFromUrl from '../_shared/insert-image-from-url';

/**
 * Inspector for a single avatar: identity (name), accessibility (alt), link,
 * and image replace/remove.
 */
export default function Inspector({ attributes, setAttributes }) {
  const { name, alt, linkUrl, newTab, url } = attributes;

  const onSelectImage = (media) => {
    setAttributes({ mediaId: media.id, url: media.url, alt: media.alt || alt || '' });
  };

  const onRemoveImage = () => {
    setAttributes({ mediaId: 0, url: '' });
  };

  // An image inserted by link has no attachment row, so the id stays 0. Both
  // render paths (render.php and AvatarGroup::render) read `url` and esc_url it,
  // so an external URL renders exactly like a library pick. Alt is left alone —
  // unlike a library pick there is nothing to read it from, and it is authored
  // by hand in the panel above.
  const onSelectImageUrl = (value) => {
    setAttributes({ mediaId: 0, url: value });
  };

  return (
    <InspectorControls>
      <PanelBody title={__('Avatar', 'brandy-blocks')} initialOpen>
        <TextControl
          label={__('Name', 'brandy-blocks')}
          value={name}
          onChange={(value) => setAttributes({ name: value })}
          help={__('Used for the hover tooltip and the initials fallback.', 'brandy-blocks')}
        />
        <TextControl
          label={__('Alt text', 'brandy-blocks')}
          value={alt}
          onChange={(value) => setAttributes({ alt: value })}
          help={__('Image alternative text. Falls back to the name when empty.', 'brandy-blocks')}
        />
        <TextControl
          label={__('Link URL', 'brandy-blocks')}
          type="url"
          value={linkUrl}
          onChange={(value) => setAttributes({ linkUrl: value })}
          placeholder="https://"
        />
        <ToggleControl
          label={__('Open link in new tab', 'brandy-blocks')}
          checked={!!newTab}
          onChange={(value) => setAttributes({ newTab: value })}
        />
      </PanelBody>

      <PanelBody title={__('Image', 'brandy-blocks')}>
        <MediaUploadCheck>
          <Flex gap={2} justify="flex-start">
            <MediaUpload
              onSelect={onSelectImage}
              allowedTypes={['image']}
              value={attributes.mediaId}
              render={({ open }) => (
                <Button variant="secondary" onClick={open}>
                  {url ? __('Replace image', 'brandy-blocks') : __('Select image', 'brandy-blocks')}
                </Button>
              )}
            />
            {url && (
              <Button variant="tertiary" isDestructive onClick={onRemoveImage}>
                {__('Remove image', 'brandy-blocks')}
              </Button>
            )}
          </Flex>
        </MediaUploadCheck>
        {/* Outside MediaUploadCheck on purpose: inserting by link never touches
            the media library, so it needs no upload rights. */}
        <InsertImageFromUrl onSubmit={onSelectImageUrl} />
      </PanelBody>
    </InspectorControls>
  );
}
